import React, { useEffect, useMemo, useState } from 'react';
import Slider from '@mui/material/Slider';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import StarIcon from '@mui/icons-material/Star';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

/* ───────────── static option lists ───────────── */
const categories = [
  { key: 'adventure', label: 'Adventure' },
  { key: 'cultural', label: 'Cultural' },
  { key: 'relaxation', label: 'Relaxation' },
  { key: 'wildlife', label: 'Wildlife' },
  { key: 'off-beat', label: 'Off-beat' },
  { key: 'handpicked', label: 'Handpicked' },
];
const ratings = [4.5, 4, 3.5, 3];
const durations = [
  { key: '1-3', label: '1 – 3 days', min: 1, max: 3 },
  { key: '4-7', label: '4 – 7 days', min: 4, max: 7 },
  { key: '8-10', label: '8 – 10 days', min: 8, max: 10 },
  { key: '10+', label: 'Over 10 days', min: 11, max: Infinity }, // same bounds Tours.js filters with
];
const packageTypes = [
  { key: 'group', label: 'Group tour', sub: 'Fixed dates · guided' },
  { key: 'private', label: 'Private tour', sub: 'Your dates · tailor-made' },
];

const LOCATIONS_PREVIEW = 6;       // how many locations are visible before "Show all"
const MIN_PRICE = 1000;
const fmt = (v) => `₹${Number(v).toLocaleString('en-IN')}`;
const daysOf = (t) => Number(t.days) || Number(t.duration?.match(/\d+/)?.[0]) || 0;

/* ───────────── location normalisation ─────────────
   "  manali ", "MANALI", "Manali" all collapse to the same key ("manali"),
   so each place is listed and matched only once.
   Import `locKey` in Tours.js and use it when filtering tours by location. */
export const locKey = (s) => String(s || '').trim().replace(/\s+/g, ' ').toLowerCase();
export const locLabel = (s) => locKey(s).replace(/(^|[\s-])([a-z])/g, (m, p, c) => p + c.toUpperCase());

/* ───────────── small UI pieces ───────────── */
const Section = ({ title, count = 0, open, onToggle, children }) => (
  <div className="py-5 border-t border-blue-100 first:border-t-0 first:pt-0">
    <button type="button" onClick={onToggle} className="w-full flex items-center justify-between">
      <span className="flex items-center gap-2 font-bold text-xs uppercase tracking-wider text-slate-500">
        {title}
        {count > 0 && (
          <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center tracking-normal">
            {count}
          </span>
        )}
      </span>
      <ExpandMoreIcon sx={{ fontSize: 20 }} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
    </button>
    {open && <div className="mt-3">{children}</div>}
  </div>
);

// checkbox (multi) or radio (single) row with an optional live count
const Option = ({ active, onClick, radio = false, count, sub, children }) => {
  const disabled = count === 0 && !active;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`group w-full flex items-center gap-3 py-1.5 text-left ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
    >
      <span
        className={`w-[18px] h-[18px] shrink-0 flex items-center justify-center border-2 transition ${radio ? 'rounded-full' : 'rounded'} ${
          active ? 'bg-blue-600 border-blue-600' : 'border-slate-300 group-hover:border-blue-400'
        }`}
      >
        {active && (radio ? <span className="w-1.5 h-1.5 rounded-full bg-white" /> : <CheckIcon sx={{ fontSize: 13, color: '#fff' }} />)}
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block text-sm ${active ? 'font-semibold text-blue-700' : 'text-slate-700'}`}>{children}</span>
        {sub && <span className="block text-[11px] text-slate-400">{sub}</span>}
      </span>
      {count != null && <span className="text-xs text-slate-400 tabular-nums">{count}</span>}
    </button>
  );
};

/* ───────────── shared helpers ───────────── */
const useActive = ({ priceRange, maxPrice, selectedCategories, selectedRating, selectedDuration, selectedPackageTypes, selectedLocations }) => {
  const cats = selectedCategories.includes('all') ? [] : selectedCategories;
  const priceChanged = priceRange[0] !== MIN_PRICE || priceRange[1] !== maxPrice;
  const total =
    (cats.length ? 1 : 0) + (priceChanged ? 1 : 0) + (selectedRating ? 1 : 0) + (selectedDuration ? 1 : 0) +
    (selectedPackageTypes.length ? 1 : 0) + (selectedLocations.length ? 1 : 0);
  return { cats, priceChanged, total };
};

/* ───────────── applied-filter chips (render above the tour grid) ───────────── */
export function ActiveFilterChips(props) {
  const {
    priceRange, onPriceChange, maxPrice = 50000,
    onCategoryChange, selectedRating, onRatingChange, selectedDuration, onDurationChange,
    selectedPackageTypes, onPackageTypeChange, selectedLocations, onLocationChange, onClearAll,
  } = props;
  const { cats, priceChanged, total } = useActive({ ...props, maxPrice });
  if (!total) return null;

  const Chip = ({ label, onRemove }) => (
    <span className="inline-flex items-center gap-1 pl-3 pr-1.5 py-1 bg-blue-50 text-blue-700 text-xs font-semibold rounded-full border border-blue-100">
      {label}
      <button type="button" onClick={onRemove} aria-label={`Remove ${label}`} className="w-4 h-4 rounded-full hover:bg-blue-200 flex items-center justify-center">
        <CloseIcon sx={{ fontSize: 12 }} />
      </button>
    </span>
  );

  // one chip per place even if the selected list somehow holds spelling variants
  const seenLoc = new Set();
  const uniqueSelectedLocations = selectedLocations.filter((l) => {
    const k = locKey(l);
    if (seenLoc.has(k)) return false;
    seenLoc.add(k);
    return true;
  });

  return (
    <div className="flex flex-wrap items-center gap-2">
      {priceChanged && <Chip label={`${fmt(priceRange[0])} – ${fmt(priceRange[1])}`} onRemove={() => onPriceChange([MIN_PRICE, maxPrice])} />}
      {selectedPackageTypes.map((k) => <Chip key={k} label={packageTypes.find((p) => p.key === k)?.label || k} onRemove={() => onPackageTypeChange(k)} />)}
      {cats.map((k) => <Chip key={k} label={categories.find((c) => c.key === k)?.label || k} onRemove={() => onCategoryChange(k)} />)}
      {selectedDuration && <Chip label={durations.find((d) => d.key === selectedDuration)?.label} onRemove={() => onDurationChange(null)} />}
      {selectedRating && <Chip label={`${selectedRating}★ & above`} onRemove={() => onRatingChange(null)} />}
      {uniqueSelectedLocations.map((l) => <Chip key={locKey(l)} label={locLabel(l)} onRemove={() => onLocationChange(l)} />)}
      <button type="button" onClick={onClearAll} className="text-xs font-semibold text-slate-500 hover:text-blue-600 underline ml-1">Clear all</button>
    </div>
  );
}

/* ───────────── sidebar ───────────── */
export function TourFilterSidebar(props) {
  const {
    priceRange, onPriceChange, maxPrice = 50000,
    selectedCategories, onCategoryChange, selectedRating, onRatingChange,
    selectedDuration, onDurationChange, selectedPackageTypes, onPackageTypeChange,
    onClearAll, selectedLocations, onLocationChange, availableLocations = [],
    tours,                 // optional: tours the filters apply to → enables live counts + histogram
    priceOf = (t) => Number(t.display_price || t.price || 0),
    resultCount,           // optional: shown on the mobile "Show N tours" button
    embedded = false,      // true inside the mobile drawer (no card chrome / sticky)
    onApply,               // mobile "Show N tours" button handler (closes drawer)
  } = props;

  const [open, setOpen] = useState({ price: true, pkg: true, cat: true, dur: true, rat: false, loc: true });
  // Slider keeps its own value while dragging; the (expensive) filter only runs on release.
  const [dragRange, setDragRange] = useState(priceRange);
  useEffect(() => { setDragRange(priceRange); }, [priceRange]);
  const [locSearch, setLocSearch] = useState('');
  const [showAllLoc, setShowAllLoc] = useState(false);
  const flip = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));
  const { cats, priceChanged, total } = useActive({ ...props, maxPrice });
  const hasCounts = Array.isArray(tours);

  // normalised keys of the selected locations
  const selLocKeys = useMemo(() => selectedLocations.map(locKey), [selectedLocations]);

  /* count helpers: a facet's count ignores its OWN selection, applies all the others */
  const pass = useMemo(() => {
    const catSel = selectedCategories.includes('all') ? [] : selectedCategories;
    const both = selectedPackageTypes.includes('group') && selectedPackageTypes.includes('private');
    return (t, skip) =>
      (skip === 'price' || (priceOf(t) >= priceRange[0] && priceOf(t) <= priceRange[1])) &&
      (skip === 'cat' || !catSel.length || catSel.includes(t.category)) &&
      (skip === 'rat' || !selectedRating || Number(t.rating || 0) >= selectedRating) &&
      (skip === 'dur' || !selectedDuration || (() => {
        const d = durations.find((x) => x.key === selectedDuration);
        const n = daysOf(t);
        return d ? n >= d.min && n <= d.max : true;
      })()) &&
      (skip === 'pkg' || !selectedPackageTypes.length || both ||
        (selectedPackageTypes.includes('group') ? t.is_group_tour === true : t.is_group_tour !== true)) &&
      (skip === 'loc' || !selLocKeys.length || selLocKeys.includes(locKey(t.location)));
  }, [priceRange, selectedCategories, selectedRating, selectedDuration, selectedPackageTypes, selLocKeys, priceOf]);

  const count = (skip, fn) => (hasCounts ? tours.filter((t) => pass(t, skip) && fn(t)).length : undefined);

  /* price histogram (12 bars) */
  const bars = useMemo(() => {
    if (!hasCounts || !tours.length) return null;
    const b = Array(12).fill(0);
    tours.forEach((t) => { b[Math.min(11, Math.floor((priceOf(t) / maxPrice) * 12))]++; });
    const m = Math.max(...b, 1);
    return b.map((v) => v / m);
  }, [tours, hasCounts, maxPrice, priceOf]);

  /* locations: de-duplicated by normalised key (case / extra spaces ignored),
     most tours first, selected ones pinned on top */
  const locations = useMemo(() => {
    const unique = new Map(); // key -> display label
    availableLocations.forEach((l) => {
      const k = locKey(l);
      if (k && !unique.has(k)) unique.set(k, locLabel(l));
    });
    const rows = [...unique].map(([k, l]) => ({ k, l, n: count('loc', (t) => locKey(t.location) === k) }));
    rows.sort((a, b) =>
      (selLocKeys.includes(b.k) - selLocKeys.includes(a.k)) ||
      ((b.n ?? 0) - (a.n ?? 0)) ||
      a.l.localeCompare(b.l)
    );
    return rows;
    // eslint-disable-next-line
  }, [availableLocations, tours, pass, selLocKeys]);

  const matchedLoc = locations.filter(({ l }) => l.toLowerCase().includes(locSearch.trim().toLowerCase()));
  const visibleLoc = locSearch || showAllLoc ? matchedLoc : matchedLoc.slice(0, LOCATIONS_PREVIEW);

  // toggle by key so a differently-spelled selected entry gets removed instead of duplicated
  const toggleLocation = (k, label) => {
    const existing = selectedLocations.find((s) => locKey(s) === k);
    onLocationChange(existing !== undefined ? existing : label);
  };

  const shell = embedded
    ? 'flex flex-col'
    : 'bg-white rounded-2xl border border-blue-200 border-t-4 border-t-blue-600 shadow-[0_10px_25px_-8px_rgba(15,41,66,0.18)] sticky top-32 flex flex-col overflow-hidden';

  return (
    <div className={shell} style={embedded ? undefined : { maxHeight: 'calc(100vh - 140px)' }}>
      {/* Header */}
      <div className={`shrink-0 flex items-center justify-between ${embedded ? 'pb-3 border-b border-slate-100' : 'px-6 py-4 border-b border-blue-100 bg-blue-50/70'}`}>
        <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
          <FilterAltIcon sx={{ fontSize: 20 }} className="text-blue-600" />
          Filters
          {total > 0 && <span className="text-[11px] font-bold text-blue-700 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-full">{total} active</span>}
        </h3>
        <button type="button" onClick={onClearAll} className="text-xs text-blue-600 font-semibold hover:underline">Clear all</button>
      </div>

      <div
        className={`${embedded ? 'pt-5' : 'flex-1 overflow-y-auto px-6 py-5'} [&::-webkit-scrollbar]:hidden`}
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {/* Price – drag only: track is NOT clickable, and the page can scroll over it */}
        <Section title="Price range" count={priceChanged ? 1 : 0} open={open.price} onToggle={() => flip('price')}>
          {bars && (
            <div className="flex items-end gap-[3px] h-10 mb-1 px-1.5" aria-hidden="true">
              {bars.map((v, i) => {
                const mid = ((i + 0.5) / 12) * maxPrice;
                const inside = mid >= priceRange[0] && mid <= priceRange[1];
                return <i key={i} className={`flex-1 rounded-sm ${inside ? 'bg-blue-300' : 'bg-slate-200'}`} style={{ height: `${Math.max(8, v * 100)}%` }} />;
              })}
            </div>
          )}
          <div className="px-2.5">
            <Slider
              value={dragRange}
              onChange={(e, v) => setDragRange(v)}
              onChangeCommitted={(e, v) => onPriceChange(v)}
              valueLabelDisplay="auto"
              valueLabelFormat={fmt}
              min={MIN_PRICE}
              max={maxPrice}
              step={500}
              disableSwap
              sx={{
                color: '#2563eb',
                height: 5,
                // root ignores pointer events → clicking the rail does nothing and touch-scroll passes through
                pointerEvents: 'none',
                touchAction: 'pan-y',
                // only the two handles can be grabbed
                '& .MuiSlider-thumb': {
                  pointerEvents: 'auto',
                  touchAction: 'none',
                  width: 20,
                  height: 20,
                  backgroundColor: '#fff',
                  border: '3px solid #2563eb',
                  '&:hover, &.Mui-focusVisible, &.Mui-active': { boxShadow: '0 0 0 8px rgba(37, 99, 235, 0.16)' },
                },
                '& .MuiSlider-rail': { backgroundColor: '#e2e8f0', opacity: 1 },
                '& .MuiSlider-valueLabel': { backgroundColor: '#1d4ed8', borderRadius: '8px', fontSize: 11 },
              }}
            />
          </div>
          <div className="flex justify-between items-center mt-2 text-sm font-semibold text-slate-700">
            <span>{fmt(dragRange[0])}</span>
            <span className="text-blue-600 font-bold">{fmt(dragRange[1])}</span>
          </div>
          <p className="mt-1 text-[11px] text-slate-400">Drag the handles to set your range</p>
        </Section>

        {/* Package type */}
        <Section title="Package type" count={selectedPackageTypes.length} open={open.pkg} onToggle={() => flip('pkg')}>
          {packageTypes.map((p) => (
            <Option
              key={p.key}
              active={selectedPackageTypes.includes(p.key)}
              onClick={() => onPackageTypeChange(p.key)}
              sub={p.sub}
              count={count('pkg', (t) => (p.key === 'group' ? t.is_group_tour === true : t.is_group_tour !== true))}
            >
              {p.label}
            </Option>
          ))}
        </Section>

        {/* Category */}
        <Section title="Category" count={cats.length} open={open.cat} onToggle={() => flip('cat')}>
          {categories.map((c) => (
            <Option key={c.key} active={selectedCategories.includes(c.key)} onClick={() => onCategoryChange(c.key)} count={count('cat', (t) => t.category === c.key)}>
              {c.label}
            </Option>
          ))}
        </Section>

        {/* Duration */}
        <Section title="Duration" count={selectedDuration ? 1 : 0} open={open.dur} onToggle={() => flip('dur')}>
          {durations.map((d) => {
            const active = selectedDuration === d.key;
            return (
              <Option key={d.key} radio active={active} onClick={() => onDurationChange(active ? null : d.key)}
                count={count('dur', (t) => daysOf(t) >= d.min && daysOf(t) <= d.max)}>
                {d.label}
              </Option>
            );
          })}
        </Section>

        {/* Rating */}
        <Section title="Guest rating" count={selectedRating ? 1 : 0} open={open.rat} onToggle={() => flip('rat')}>
          {ratings.map((r) => {
            const active = selectedRating === r;
            return (
              <Option key={r} radio active={active} onClick={() => onRatingChange(active ? null : r)} count={count('rat', (t) => Number(t.rating || 0) >= r)}>
                <span className="inline-flex items-center gap-1"><StarIcon sx={{ fontSize: 16, color: '#f59e0b' }} />{r} & above</span>
              </Option>
            );
          })}
        </Section>

        {/* Location – each place once, top 6 by tour count, search + "Show all" for the rest */}
        <Section title="Location" count={selLocKeys.length} open={open.loc} onToggle={() => flip('loc')}>
          <div className="relative mb-2">
            <SearchIcon sx={{ fontSize: 18 }} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={locSearch}
              onChange={(e) => setLocSearch(e.target.value)}
              placeholder={`Search ${locations.length} locations...`}
              className="w-full text-sm font-medium bg-blue-50/60 border border-blue-200 rounded-xl pl-9 pr-3 py-2.5 focus:outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition"
            />
          </div>

          <div className={showAllLoc || locSearch ? 'max-h-64 overflow-y-auto pr-1' : ''}>
            {visibleLoc.length === 0 && <p className="text-xs text-slate-400 py-2">No places found</p>}
            {visibleLoc.map(({ k, l, n }) => (
              <Option key={k} active={selLocKeys.includes(k)} onClick={() => toggleLocation(k, l)} count={n}>{l}</Option>
            ))}
          </div>

          {!locSearch && matchedLoc.length > LOCATIONS_PREVIEW && (
            <button type="button" onClick={() => setShowAllLoc((s) => !s)} className="mt-1 text-xs font-semibold text-blue-600 hover:underline">
              {showAllLoc ? 'Show less' : `Show all ${matchedLoc.length} locations`}
            </button>
          )}
        </Section>
      </div>

      {/* Mobile drawer: sticky apply button */}
      {embedded && onApply && (
        <div className="sticky bottom-0 -mx-4 mt-2 px-4 py-3 bg-white border-t border-slate-100">
          <button type="button" onClick={onApply} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl shadow-lg shadow-blue-600/25">
            {resultCount != null ? `Show ${resultCount} ${resultCount === 1 ? 'tour' : 'tours'}` : 'Apply filters'}
          </button>
        </div>
      )}
    </div>
  );
}

export default TourFilterSidebar;